import React, { useMemo } from 'react';

import { classNames } from '@/helpers/classNames';
import { Capacitor } from '@capacitor/core';

import styles from './IndexIndicator.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export type ConditionalItem = {
	index: number;
	visible: boolean;
};

export type DisabledItem = {
	index: number;
	enabled: boolean;
};

export type IndexIndicatorProps = {
	current: number;
	total: number;
	className?: string;
	activeClassName?: string;
	dotClassName?: string;
	variant?: 'regular' | 'stretched';
	onIndicatorClick?: (indicatorIndex: number) => void;
	hasHover?: boolean;
	/** Hide specific indices. E.g. [{ index: 3, visible: false }] hides the 4th item. onIndicatorClick still receives the logical index. */
	conditionalItems?: ConditionalItem[];
	disabledItems?: DisabledItem[];
	/** Logical indices that have associated data. Future dots in this list (and not disabled) render as light blue and are clickable. */
	hasDataItems?: number[];
};

export const IndexIndicator: React.FC<IndexIndicatorProps> = ({
	current,
	total,
	className,
	activeClassName,
	dotClassName,
	variant = 'regular',
	onIndicatorClick,
	hasHover = false,
	conditionalItems = [],
	disabledItems = [],
	hasDataItems = [],
}) => {
	const visibleIndices = useMemo(() => {
		const visibilityByIndex = new Map(
			conditionalItems.map((item) => [item.index, item.visible]),
		);
		return Array.from({ length: total }, (_v, i) => i).filter(
			(i) => visibilityByIndex.get(i) !== false,
		);
	}, [total, conditionalItems]);

	const currentVisiblePosition = useMemo(() => {
		const idx = visibleIndices.indexOf(current);
		if (idx >= 0) return idx;
		// Current step is hidden; show the last visible index before it as active
		const lastBefore = visibleIndices
			.map((idx, pos) => (idx < current ? pos : -1))
			.filter((p) => p >= 0)
			.pop();
		return lastBefore ?? 0;
	}, [visibleIndices, current]);

	return (
		<div
			className={classNames([
				styles['indicator'],
				styles[`variant-${variant}`],
				className,
			])}
		>
			{visibleIndices.map((logicalIndex, pos) => {
				const isDisabled = disabledItems.some(
					(item) => item.index === logicalIndex && !item.enabled,
				);
				const isFuture = pos > currentVisiblePosition;
				const hasData = hasDataItems.includes(logicalIndex);
				const showAsCompleted =
					pos < currentVisiblePosition ||
					(isFuture && hasData && !isDisabled);

				return (
					<div
						key={logicalIndex}
						className={classNames([
							styles['dot'],
							dotClassName,
							showAsCompleted && styles['completed'],
							pos === currentVisiblePosition &&
								(activeClassName || styles['active']),
							pos !== currentVisiblePosition &&
								!isCapacitor &&
								hasHover &&
								!isDisabled &&
								!(isFuture && !hasData) &&
								styles['hover'],
						])}
						onClick={() => {
							if (isDisabled) return;
							if (isFuture && !hasData) return;
							logicalIndex !== current &&
								onIndicatorClick &&
								onIndicatorClick(logicalIndex);
						}}
					/>
				);
			})}
		</div>
	);
};

export default IndexIndicator;
